import React from 'react';
import { Activity } from 'lucide-react';
import { formatInr, formatSignedInr } from '../../../utils/gmpBoard';

const isNumber = (value) => value !== null && value !== undefined;

export const CompanyGmpRange = ({ row }) => {
  // Unquoted issues report a 0/0 range; treat that as "no trades" rather than a flat ₹0
  const low = row.hasGmp ? row.gmpDayLow : null;
  const high = row.hasGmp ? row.gmpDayHigh : null;
  const hasRange = isNumber(low) && isNumber(high);
  const flat = hasRange && high === low;
  const position =
    hasRange && row.hasGmp && !flat
      ? Math.min(100, Math.max(0, ((row.gmp - low) / (high - low)) * 100))
      : 50;

  const checkpoints = [
    { label: 'Opening-day GMP', value: row.openDayGmp },
    { label: 'Closing-day GMP', value: row.closeDayGmp },
    { label: "Today's low", value: low },
    { label: "Today's high", value: high },
    { label: 'Latest GMP', value: row.gmp, emphasis: true },
  ];

  return (
    <section aria-labelledby="gmp-range" className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-4 sm:p-6">
      <h2 id="gmp-range" className="flex items-center gap-2 text-base font-extrabold text-slate-900">
        <Activity className="w-4 h-4 text-[#008080]" />
        GMP Movement
      </h2>
      <p className="text-xs text-slate-500 mt-0.5">
        Today&apos;s trading range in the grey market and the premium recorded at key dates.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-5 lg:grid-cols-1 xl:grid-cols-5 gap-5 mt-5">
        {/* Range bar */}
        <div className="md:col-span-3 lg:col-span-1 xl:col-span-3 rounded-xl border border-slate-200 bg-slate-50/50 p-4 sm:p-5">
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Today&apos;s GMP range</div>
          {hasRange ? (
            <>
              <div className="relative mt-8 mb-2">
                <div className="h-2.5 rounded-full bg-gradient-to-r from-rose-200 via-amber-200 to-emerald-300" />
                {row.hasGmp && (
                  <div className="absolute -top-7 -translate-x-1/2 flex flex-col items-center" style={{ left: `${position}%` }}>
                    <span className="px-1.5 py-0.5 rounded-md bg-slate-900 text-white text-[10px] font-bold whitespace-nowrap">
                      {formatSignedInr(row.gmp)}
                    </span>
                    <span className="w-3.5 h-3.5 mt-1 rounded-full bg-white border-[3px] border-slate-900 shadow" />
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between text-xs font-semibold text-slate-600">
                <span>Low {formatSignedInr(low)}</span>
                <span>High {formatSignedInr(high)}</span>
              </div>
              <p className="text-[11px] text-slate-500 mt-3">
                {flat
                  ? `The premium has held steady at ${formatSignedInr(low)} today.`
                  : `The premium has moved ${formatInr(high - low)} between today's low and high.`}
              </p>
            </>
          ) : (
            <p className="text-sm text-slate-500 mt-3">No grey market trades have been recorded for this issue today.</p>
          )}
        </div>

        {/* Checkpoints */}
        <dl className="md:col-span-2 lg:col-span-1 xl:col-span-2 divide-y divide-slate-100 rounded-xl border border-slate-200">
          {checkpoints.map(({ label, value, emphasis }) => (
            <div key={label} className={`flex items-center justify-between gap-3 px-4 py-2.5 ${emphasis ? 'bg-teal-50/60' : ''}`}>
              <dt className={`text-xs ${emphasis ? 'font-bold text-[#006666]' : 'text-slate-500'}`}>{label}</dt>
              <dd
                className={`text-sm font-extrabold ${
                  !isNumber(value) ? 'text-slate-300' : value > 0 ? 'text-emerald-600' : value < 0 ? 'text-rose-600' : 'text-slate-700'
                }`}
              >
                {isNumber(value) ? formatSignedInr(value) : '—'}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
};

export default CompanyGmpRange;
